import { type ReactNode, useState } from "react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../components/ui/accordion";
import { Button } from "../../../components/ui/button";
import { RECENTLY_CLOSED_PREVIEW_COUNT } from "../../../handlers/jobStatus";

const ITEM_VALUE = "recently-closed";

/**
 * "page" is the full-width Jobs tab, "card" the narrower verified-profile panel. The section is
 * identical on both; only the type scale differs, so the call sites pick a variant rather than
 * passing class names for every part.
 */
const VARIANT_STYLES = {
	page: {
		heading: "text-2xl font-semibold font-plus-jakarta",
		count: "ml-2 font-normal text-gray-500",
		description: "text-sm text-gray-600 mb-2 pt-1",
		list: "",
	},
	card: {
		heading: "text-sm font-semibold text-foreground",
		count: "ml-2 font-normal text-muted-foreground",
		description: "mb-3 pt-1 text-sm text-muted-foreground",
		list: "space-y-3",
	},
} as const;

interface RecentlyClosedJobsProps<T> {
	jobs: T[];
	renderJob: (job: T) => ReactNode;
	/**
	 * Expanded on mount for a company with nothing open, where these rows are the only content the
	 * Jobs tab has — collapsing them would leave a visitor looking at an empty tab.
	 */
	defaultOpen?: boolean;
	variant?: keyof typeof VARIANT_STYLES;
	className?: string;
}

function RecentlyClosedJobs<T>({
	jobs,
	renderJob,
	defaultOpen = false,
	variant = "page",
	className,
}: RecentlyClosedJobsProps<T>) {
	const [showAll, setShowAll] = useState(false);

	if (jobs.length === 0) return null;

	const styles = VARIANT_STYLES[variant];
	const Heading = variant === "page" ? "h2" : "h3";
	// Newest closed first — the ordering comes from splitJobsByStatus.
	const visibleJobs = showAll ? jobs : jobs.slice(0, RECENTLY_CLOSED_PREVIEW_COUNT);
	const hiddenCount = jobs.length - visibleJobs.length;

	return (
		<Accordion type="single" collapsible defaultValue={defaultOpen ? ITEM_VALUE : undefined} className={className}>
			<AccordionItem value={ITEM_VALUE} className="border-b-0">
				<AccordionTrigger className="px-0 py-0 hover:no-underline">
					<Heading className={styles.heading}>
						Recently Closed
						<span className={styles.count}>({jobs.length})</span>
					</Heading>
				</AccordionTrigger>
				<AccordionContent>
					<p className={styles.description}>These roles are no longer accepting applications.</p>
					<div className={styles.list}>{visibleJobs.map((job) => renderJob(job))}</div>
					{hiddenCount > 0 && (
						<Button type="button" variant="link" className="mt-3 px-0" onClick={() => setShowAll(true)}>
							Show {hiddenCount} more closed {hiddenCount === 1 ? "role" : "roles"}
						</Button>
					)}
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
}

export default RecentlyClosedJobs;
